import React, { useState, useEffect, useRef, useMemo } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import ListeningStatus from "Components/Global/ListeningStatus";
import { getFTUEData, SYLLABLE_TYPES_PRIORITY } from "./helpers";
import { Container } from "../shared";
import useConversation from "../shared/useConversation";
import { listen } from "Services/Listen";
import "./SyllableSegmentation.scss";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";
import SyllableSegmentationContent from "./SyllableSegmentationContent";
import SyllableSegmentationResultService from "./result-service";
import { getUserData } from "Services/API";
import RulesIntro from "./RulesIntro";

let ttsStrings;

export default function SyllableSegmentationValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.syllable_segmentation;

  let isValid = false;

  const { word, logSessionInfo } = props;

  let activeType = null;
  if (word) {
    for (let i = 0; i < SYLLABLE_TYPES_PRIORITY.length; i++) {
      if (word.includes(SYLLABLE_TYPES_PRIORITY[i])) {
        activeType = SYLLABLE_TYPES_PRIORITY[i];
        isValid = true;
        console.log(`Selected multisyllable type: ${activeType}`);
        break;
      }
    }
  }

  const isMountedRef = useIsMounted();

  const [hardMode, setHardMode] = useState(
    SyllableSegmentationResultService.isHardMode
  );

  useEffect(() => {
    setHardMode(SyllableSegmentationResultService.isHardMode);
  }, [SyllableSegmentationResultService.isHardMode]);

  useEffect(() => {
    if (!isValid) close();
  }, []);

  if (!isValid) return null;

  const userLocale = useMemo(() => getUserData().locale, []);
  const tutorInSpanish = useMemo(() => getUserData().tutorInSpanish, []);
  const ftueData = getFTUEData(userLocale, tutorInSpanish, activeType);
  const position = word.indexOf(":") + 1;
  // Example of a word: "VC/CV:rAb/b$Its",
  // but this intervention only needs the following part "VC/CV:rab/bits"
  const parsedWord =
    word.slice(0, position) +
    word.slice(position).toLowerCase().replaceAll("$", "");

  return (
    <SyllableSegmentation
      {...props}
      activeType={activeType}
      ftueData={ftueData}
      close={close}
      dispatchEvent={dispatchEvent}
      isMountedRef={isMountedRef}
      hardMode={hardMode}
      logSessionInfo={logSessionInfo}
      word={parsedWord}
    />
  );

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function SyllableSegmentation(props) {
  const {
    activeType,
    speech,
    ftue,
    ftueData,
    hardMode,
    logSessionInfo,
    word,
    close,
    ftueCb,
  } = props;

  const STATES = {
    dnd: "dnd",
    dnd_idle_1: "dnd_idle_1",
    dnd_idle_2: "dnd_idle_2",
    dnd_wrong: "dnd_wrong",
    dnd_correct: "dnd_correct",
    response: "response",
    response_idle_1: "response_idle_1",
    response_idle_2: "response_idle_2",
    response_wrong: "response_wrong",
    response_correct: "response_correct",
  };

  const needsFTUERef = useRef(ftue.indexOf(activeType) === -1 ? true : false);
  const [stage, setStage] = useState(needsFTUERef.current ? 0 : 1);
  const [correctWord, setCorrectWord] = useState("");
  const [parsedWord, setParsedWord] = useState("");
  const [preview, setPreview] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [interactive, setInteractive] = useState(false);
  const [puzzleEnded, setPuzzleEnded] = useState(false);
  const [currentState, setCurrentState] = useState(STATES.dnd);
  const [showIntroFTUE, setShowIntroFTUE] = useState(false);
  const [showSegmentation, setShowSegmentation] = useState(false);
  const [showMic, setShowMic] = useState(false);
  const { speak, listenWord, abortSpeech } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const timer = useRef(undefined);
  const incorrectCount = useRef(0);
  const handlingIncorrect = useRef(false);
  const correctWordRef = useRef(correctWord);
  const puzzleEndedRef = useRef(false);
  const hardModeRef = useRef(hardMode);
  const currentStateRef = useRef(STATES.dnd);
  const stageRef = useRef(stage);

  useEffect(() => {
    if (!props.isMountedRef.current) {
      return;
    }

    correctWordRef.current = correctWord;
  }, [correctWord]);

  useEffect(() => {
    if (!props.isMountedRef.current) {
      return;
    }

    puzzleEndedRef.current = puzzleEnded;
  }, [puzzleEnded]);

  useEffect(() => {
    if (!props.isMountedRef.current) {
      return;
    }

    hardModeRef.current = hardMode;
  }, [hardMode]);

  // handle stages
  useEffect(() => {
    onStageChange();
    stageRef.current = stage;
  }, [stage]);

  useEffect(() => {
    const resetTimer = () => {
      if (timer.current) {
        clearTimeout(timer.current);
        startGuessTimer();
      }
    };
    document.addEventListener("mousemove", resetTimer);
    document.addEventListener("keydown", resetTimer);
    document.addEventListener("mousedown", resetTimer);
    document.addEventListener("pointerdown", resetTimer);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      document.removeEventListener("mousemove", resetTimer);
      document.removeEventListener("keydown", resetTimer);
      document.removeEventListener("mousedown", resetTimer);
      document.removeEventListener("pointerdown", resetTimer);
    };
  }, []);

  useEffect(() => {
    decodeWord(word);
  }, [word]);

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    currentStateRef.current = currentState;
    if (
      currentState === STATES.response_idle_2 ||
      currentState === STATES.response_wrong
    ) {
      const [syllable1, syllable2] = word
        .substr(word.indexOf(":") + 1)
        .split("/");

      handleStateSpeak(
        ttsStrings[currentState]({
          syllable1: syllable1,
          syllable2: syllable2,
          word: parsedWord,
        })
      );
    } else if (currentState === STATES.response_correct) {
      handleStateSpeak(ttsStrings[currentState]({ word: parsedWord }));
    } else {
      handleStateSpeak(ttsStrings[currentState]);
    }
    if (
      [
        STATES.response,
        STATES.response_idle_1,
        STATES.response_idle_2,
      ].includes(currentState)
    ) {
      setShowMic(true);
    }
  }, [currentState]);

  const decodeWord = (str) => {
    const index = str.indexOf(":") + 1;
    setParsedWord(str.substring(index, str.length).replaceAll("/", ""));
  };

  const handleStateSpeak = async (text) => {
    await cancellablePromise(speak(text));
    if (
      currentState === STATES.dnd_idle_2 ||
      currentState === STATES.dnd_wrong
    ) {
      showCorrectAnswer();
    }
    startGuessTimer();
  };

  const onStageChange = async () => {
    if (!props.isMountedRef.current) {
      return;
    }

    switch (stage) {
      case 0:
        await HandleStageZero();
        break;
      case 1:
        // await HandleStageZero();
        await HandleStageOne();
        break;
      case 2:
        // await HandleStageZero();
        await handleStageTwo();
        break;
    }
  };

  const resetTest = () => {
    setCorrectWord("");
    setShowAnswer(false);
    setPreview(false);
  };

  // FTUE only stage - FTUE is defined if invervention of this type is served for the first time in the current session.
  const HandleStageZero = async () => {
    try {
      setShowIntroFTUE(true);
      setInteractive(false);
      await cancellablePromise(speak(ttsStrings.ftue_intro));
      await cancellablePromise(sleep(1000));
      await cancellablePromise(speak(ftueData.intro_resume));
      await cancellablePromise(sleep(2500));
      setShowIntroFTUE(false);
      setShowSegmentation(true);

      decodeWord(ftueData.watchWord);
      setCorrectWord(ftueData.watchWord);

      setTimeout(() => {
        setPreview(true);
        setTimeout(() => {
          setParsedWord(ftueData.watchWord);
          setShowAnswer(true);
        }, 1500);
      }, 2500);

      await cancellablePromise(sleep(1000));
      await cancellablePromise(speak(ttsStrings.ftue_intro_watch));

      await cancellablePromise(sleep(4000));
      setPreview(false);
      decodeWord(word);
      await cancellablePromise(speak(ttsStrings.ftue_intro_your_turn));
      await cancellablePromise(sleep(1000));
      goToNextStage();
    } catch (error) {
      if (error.isCancelled) return;
      throw error;
    }
  };

  // Non-FTUE will start from this stage
  const HandleStageOne = async () => {
    try {
      resetTest();

      setShowIntroFTUE(false);
      setShowSegmentation(true);
      if (needsFTUERef.current) {
        await cancellablePromise(speak(ttsStrings.ftue_intro_your_turn_resume));
        ftueCb(activeType);
      } else {
        await cancellablePromise(speak(ttsStrings.non_ftue_intro));
      }
      setInteractive(true);
      setPuzzleEnded(false);
      startGuessTimer();
    } catch (error) {
      if (error.isCancelled) return;
      throw error;
    }
  };

  const handleSpeechResponse = async (status) => {
    if (status === "skip") {
      return close();
    }
    if (status === "match") {
      await cancellablePromise(
        speak(
          ttsStrings.response_correct({ word: parsedWord.replaceAll("/", "") })
        )
      );
    } else {
      await cancellablePromise(
        speak(
          ftueData.response_wrong({
            word: parsedWord.replaceAll("/", ""),
          })
        )
      );
    }

    await cancellablePromise(sleep(500));
    close();
  };

  const handleStageTwo = async () => {
    try {
      // resetTest();
      startGuessTimer();
      await cancellablePromise(sleep(1000));
      await cancellablePromise(
        speak({
          textToSpeak: ttsStrings.response_into,
        })
      );

      const { status } = await cancellablePromise(
        listenWord({
          targetWord: parsedWord,
          patterOverride: ["match", "struggle", "silence"],
        })
      );
      handleSpeechResponse(status);
    } catch (error) {
      if (error.isCancelled) return;
      throw error;
    }
  };

  const goToNextStage = async () => {
    if (!props.isMountedRef.current) {
      return;
    }

    if (stage === 2) {
      try {
        // End of intervention
        await cancellablePromise(speak(ttsStrings.great_work));
        props.close();
      } catch (error) {
        if (error.isCancelled) return;
        throw error;
      }
      return;
    }

    setStage(stage + 1);
  };

  const startGuessTimer = () => {
    if (stageRef.current !== 0) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        handleIdleStateChanges();
      }, 20000);
    }
  };

  const handleIdleStateChanges = () => {
    switch (currentStateRef.current) {
      case STATES.dnd:
        setCurrentState(STATES.dnd_idle_1);
        break;
      case STATES.dnd_idle_1:
        setInteractive(false);
        setCurrentState(STATES.dnd_idle_2);
        break;
      case STATES.dnd_idle_2:
        setCurrentState(STATES.response_idle_1);
        break;
      case STATES.response_idle_1:
        setCurrentState(STATES.response_idle_2);
        break;
    }
  };

  const handleGuess = async (guessWord) => {
    await abortSpeech();

    try {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      if (word.indexOf(guessWord) !== -1) {
        logSessionInfo("Syllable_Segmentation_success_" + activeType, {
          attempt: incorrectCount.current + 1,
          hardMode: hardModeRef.current,
        });
        SyllableSegmentationResultService.addPuzzleResult(1);
        setPuzzleEnded(true);
        setInteractive(false);

        await cancellablePromise(speak(ttsStrings.dnd_correct));

        setCurrentState(STATES.response);

        goToNextStage();
      } else {
        handleIncorrect();
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw error;
    }
  };

  const handleIncorrect = async () => {
    if (!props.isMountedRef.current || handlingIncorrect.current) {
      return;
    }

    handlingIncorrect.current = true;

    if (timer.current) {
      clearTimeout(timer.current);
    }

    setPuzzleEnded(true);
    setInteractive(false);

    switch (currentState) {
      case STATES.dnd:
      case STATES.dnd_idle_1:
      case STATES.dnd_idle_2:
      case STATES.dnd_wrong:
        setCurrentState(STATES.dnd_wrong);
        // await cancellablePromise(speak(ttsStrings.dnd_wrong));

        break;
      case STATES.response:
      case STATES.response_idle_1:
      case STATES.response_idle_2:
      case STATES.response_wrong: {
        setCurrentState(STATES.response_wrong);
        const index = word.indexOf(":") + 1;
        const tempWord = word.substring(index, word.length).split("/");
        await cancellablePromise(
          speak(
            ftueData.response_wrong({
              word: tempWord.join(""),
            })
          )
        );
        break;
      }
      default:
        logSessionInfo("Syllable_Segmentation_fail_" + activeType, {
          hardMode: hardModeRef.current,
        });
        return;
    }
  };

  const showCorrectAnswer = () => {
    const index = word.indexOf(":") + 1;
    setCorrectWord(word.substring(index, word.length));
    setTimeout(() => {
      setParsedWord(word.substring(index, word.length));

      setShowAnswer(true);
      setPuzzleEnded(true);
      setCurrentState(STATES.response);

      setTimeout(() => {
        goToNextStage();
      }, 3000);
    }, 2500);
  };

  return (
    <Container className="syllable-segmentation-modal">
      {ftueData && showIntroFTUE && (
        <RulesIntro
          headline={ftueData.headline}
          word={ftueData.word()}
          type={activeType}
        />
      )}
      {!showIntroFTUE && showSegmentation && (
        <SyllableSegmentationContent
          correctWord={correctWord}
          preview={preview}
          showAnswer={showAnswer}
          interactive={interactive}
          handleGuess={handleGuess}
          currentPuzzleEnded={puzzleEnded}
          word={parsedWord}
        />
      )}
      {showMic && (
        <div style={{ marginTop: "-70px" }}>
          <ListeningStatus />
        </div>
      )}
    </Container>
  );
}
